import { useEffect, useState } from "react";
import { format, parseISO, differenceInCalendarDays, eachMonthOfInterval } from "date-fns";
import { useAuth } from "../../hooks/useAuth";
import { useLanguage } from "../../hooks/useLanguage";
import {
  getAllBonusPrograms, getSalesByMonth, getAssignedPromoters, getSalesByMonthForPromoters,
} from "../../firebase/services";

// Chương trình thưởng theo model: đang chạy, sắp chạy, đã kết thúc.
// Promoter thấy số máy mình bán được trong từng chương trình + tiền thưởng tạm tính;
// supervisor thấy tổng số máy của nhóm mình phụ trách.
// Cách đếm giống computeMonthlyPay: đơn đã duyệt, đúng model, ngày bán nằm trong thời gian chương trình.
export default function BonusProgramsPage({ role = "promoter" }) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [sales, setSales] = useState([]);
  const [teamSize, setTeamSize] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openSkus, setOpenSkus] = useState({});

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const progs = (await getAllBonusPrograms().catch(() => []))
        .filter(p => p.startDate && p.endDate);
      // Các tháng mà chương trình có chạy → chỉ tải doanh số của những tháng đó
      const months = new Set();
      progs.forEach(p => {
        try {
          eachMonthOfInterval({ start: parseISO(p.startDate), end: parseISO(p.endDate) })
            .forEach(d => months.add(format(d, "yyyy-MM")));
        } catch { /* ngày lỗi → bỏ qua */ }
      });
      let list = [];
      if (role === "supervisor") {
        const team = await getAssignedPromoters(user.uid).catch(() => []);
        const ids = team.map(p => p.uid);
        if (!cancelled) setTeamSize(ids.length);
        const parts = await Promise.all([...months].map(m => getSalesByMonthForPromoters(m, ids).catch(() => [])));
        list = parts.flat();
      } else {
        const parts = await Promise.all([...months].map(m => getSalesByMonth(m, user.uid).catch(() => [])));
        list = parts.flat();
      }
      if (cancelled) return;
      setPrograms(progs);
      setSales(list);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user, role]);

  const today = format(new Date(), "yyyy-MM-dd");
  const unitsOf = (p) => sales.filter(s => p.skus?.includes(s.model)
    && s.saleDate >= p.startDate && s.saleDate <= p.endDate).length;

  // Đã thu hồi → nằm trong lịch sử, có nhãn đỏ, không tính máy/thưởng
  const revoked = (p) => p.status === "revoked";
  const running = programs.filter(p => !revoked(p) && p.startDate <= today && p.endDate >= today)
    .sort((a, b) => a.endDate.localeCompare(b.endDate));
  const upcoming = programs.filter(p => !revoked(p) && p.startDate > today)
    .sort((a, b) => a.startDate.localeCompare(b.startDate));
  const ended = programs.filter(p => revoked(p) || p.endDate < today)
    .sort((a, b) => b.endDate.localeCompare(a.endDate));

  const vnd = (v) => Number(v || 0).toLocaleString("vi-VN") + " đ";
  const dmy = (s) => s ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : "";

  const Card = ({ p, status: baseStatus }) => {
    const status = revoked(p) ? "revoked" : baseStatus;
    const units = unitsOf(p);
    const skus = Array.isArray(p.skus) ? p.skus : [];
    const showAll = openSkus[p.id];
    const visible = showAll ? skus : skus.slice(0, 6);
    const daysLeft = differenceInCalendarDays(parseISO(p.endDate), parseISO(today));
    const daysToStart = differenceInCalendarDays(parseISO(p.startDate), parseISO(today));
    const badge = status === "running" ? { cls: "badge-green", text: t("bp.st_running") }
      : status === "upcoming" ? { cls: "badge-blue", text: t("bp.st_upcoming") }
      : status === "revoked" ? { cls: "badge-red", text: t("bp.st_revoked") }
      : { cls: "badge-gray", text: t("bp.st_ended") };
    return (
      <div className="card" style={{ opacity: status === "ended" || status === "revoked" ? 0.75 : 1, marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
          <div style={{ fontWeight: 600, fontSize: 15, textDecoration: status === "revoked" ? "line-through" : "none" }}>🎁 {p.name}</div>
          <span className={`badge ${badge.cls}`} style={{ flexShrink: 0 }}>{badge.text}</span>
        </div>
        <div style={{ fontSize: 13, color: "#666", marginTop: 4 }}>
          📅 {dmy(p.startDate)} → {dmy(p.endDate)}
          {status === "running" && (
            <span style={{ color: daysLeft <= 3 ? "#C8152A" : "#15803d", fontWeight: 500 }}>
              {" · "}{daysLeft === 0 ? t("bp.last_day") : t("bp.days_left", { n: daysLeft })}
            </span>
          )}
          {status === "upcoming" && <span style={{ color: "#2563eb" }}>{" · "}{t("bp.starts_in", { n: daysToStart })}</span>}
        </div>
        {p.updatedAt?.toDate && status !== "revoked" && (
          <div style={{ fontSize: 11, color: "#B45309", marginTop: 2 }}>
            ✏️ {t("bp.updated_at", { d: format(p.updatedAt.toDate(), "dd/MM/yyyy HH:mm") })}
          </div>
        )}
        {status === "revoked" && (
          <div style={{ fontSize: 12, color: "#A32D2D", marginTop: 4 }}>
            ⛔ {t("bp.revoked_note")}{p.revokeReason ? ` — ${p.revokeReason}` : ""}
          </div>
        )}
        <div style={{ fontSize: 14, marginTop: 8 }}>
          {t("bp.per_unit")}: <b style={{ color: "#C8152A" }}>{vnd(p.amountPerUnit)}</b>
        </div>

        {status !== "upcoming" && status !== "revoked" && (
          <div style={{ display: "flex", gap: 16, marginTop: 8, padding: "8px 10px", background: "#f7f7f5", borderRadius: 8, fontSize: 13, flexWrap: "wrap" }}>
            {role === "supervisor" ? (
              <span>{t("bp.team_units", { n: units, team: teamSize })}</span>
            ) : (
              <>
                <span>{t("bp.my_units")}: <b>{units}</b></span>
                <span>{t("bp.my_bonus")}: <b style={{ color: "#15803d" }}>{vnd(units * (Number(p.amountPerUnit) || 0))}</b></span>
              </>
            )}
          </div>
        )}

        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>{t("bp.models", { n: skus.length })}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {visible.map(m => (
              <span key={m} style={{ fontSize: 12, padding: "3px 8px", borderRadius: 6, background: "#FDECEA", color: "#8a1020" }}>{m}</span>
            ))}
            {skus.length > 6 && (
              <button className="btn btn-sm" style={{ fontSize: 12, padding: "2px 8px" }}
                onClick={() => setOpenSkus(o => ({ ...o, [p.id]: !showAll }))}>
                {showAll ? t("bp.less") : t("bp.more", { n: skus.length - 6 })}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const Section = ({ title, list, status, empty }) => (
    <>
      <div style={{ fontWeight: 600, fontSize: 14, color: "#444", margin: "18px 0 8px" }}>
        {title} <span style={{ color: "#999", fontWeight: 400 }}>({list.length})</span>
      </div>
      {list.length === 0
        ? (empty && <div className="card" style={{ color: "#aaa", textAlign: "center", padding: 20 }}>{empty}</div>)
        : list.map(p => <Card key={p.id} p={p} status={status} />)}
    </>
  );

  return (
    <>
      <div className="page-header">
        <div>
          <h1>{t("bp.title")}</h1>
          <p>{role === "supervisor" ? t("bp.sub_supervisor") : t("bp.sub_promoter")}</p>
        </div>
      </div>
      {loading ? (
        <div style={{ color: "#888", padding: 20 }}>{t("common.loading")}</div>
      ) : (
        <>
          <Section title={"🔥 " + t("bp.running")} list={running} status="running" empty={t("bp.none_running")} />
          {upcoming.length > 0 && <Section title={"⏳ " + t("bp.upcoming")} list={upcoming} status="upcoming" />}
          <Section title={"📁 " + t("bp.history")} list={ended} status="ended" empty={t("bp.none_ended")} />
          {role === "promoter" && (
            <div style={{ fontSize: 11, color: "#999", marginTop: 4 }}>{t("bp.note")}</div>
          )}
        </>
      )}
    </>
  );
}
